import type { Corners, SplitLine, SplitLockMode } from './types.js';

// =============================================================================
// Source split utilities (HANDOFF §4.2, CALIBRATION_UX §2.8 / §6.1)
// =============================================================================

export const SPLIT_DEFAULT: SplitLine = {
  topX: 0.5,
  bottomX: 0.5,
  lockMode: 'center',
};

/** Floating-point tolerance for split equality checks. */
const EPSILON = 1e-4;

/** Source quad for the left wall, derived from the split seam. */
export function leftSourceQuad(s: SplitLine): Corners {
  return [
    { x: 0, y: 0 },
    { x: s.topX, y: 0 },
    { x: s.bottomX, y: 1 },
    { x: 0, y: 1 },
  ] as const;
}

/** Source quad for the right wall. */
export function rightSourceQuad(s: SplitLine): Corners {
  return [
    { x: s.topX, y: 0 },
    { x: 1, y: 0 },
    { x: 1, y: 1 },
    { x: s.bottomX, y: 1 },
  ] as const;
}

/**
 * Apply a split handle drag under the current lock mode.
 * - center: immutable (UI should disable the handle)
 * - parallel: both ends move together
 * - free: independent
 */
export function applySplitDrag(
  current: SplitLine,
  position: 'top' | 'bottom',
  x: number,
): SplitLine {
  const clamped = Math.max(0, Math.min(1, x));
  switch (current.lockMode) {
    case 'center':
      return current;
    case 'parallel':
      return { ...current, topX: clamped, bottomX: clamped };
    case 'free':
      return position === 'top'
        ? { ...current, topX: clamped }
        : { ...current, bottomX: clamped };
  }
}

// -----------------------------------------------------------------------------
// Lock mode transition (CALIBRATION_UX §6.1 9-case table)
// -----------------------------------------------------------------------------

export type LockTransition = {
  next: SplitLine;
  needsConfirm: boolean;
  /** Short one-liner for dialog title. Present only when needsConfirm. */
  reason?: string;
  /** Numeric description of what's lost. Present only when needsConfirm. */
  willLose?: string;
};

function nearDefault(topX: number, bottomX: number): boolean {
  return Math.abs(topX - 0.5) < EPSILON && Math.abs(bottomX - 0.5) < EPSILON;
}

function isTilted(topX: number, bottomX: number): boolean {
  return Math.abs(topX - bottomX) > EPSILON;
}

const fmt = (v: number): string => v.toFixed(3);

/**
 * Transition between lock modes, preserving or reconciling split values.
 * 9-case table, CALIBRATION_UX §6.1:
 *
 * | From → To         | Condition           | Confirm |
 * |-------------------|---------------------|---------|
 * | same mode         | -                   | No      |
 * | center → parallel | always              | No      |
 * | center → free     | always              | No      |
 * | parallel → free   | always              | No      |
 * | parallel → center | topX = 0.5          | No      |
 * | parallel → center | topX ≠ 0.5          | Yes     |
 * | free → parallel   | topX = bottomX      | No      |
 * | free → parallel   | topX ≠ bottomX      | Yes     |
 * | free → center     | both 0.5            | No      |
 * | free → center     | any off-center      | Yes     |
 */
export function transitionLockMode(
  current: SplitLine,
  next: SplitLockMode,
): LockTransition {
  if (current.lockMode === next) {
    return { next: current, needsConfirm: false };
  }

  switch (next) {
    case 'center': {
      const reset: SplitLine = { topX: 0.5, bottomX: 0.5, lockMode: 'center' };
      if (nearDefault(current.topX, current.bottomX)) {
        return { next: reset, needsConfirm: false };
      }
      return {
        next: reset,
        needsConfirm: true,
        reason: 'Split offset will reset to 0.5',
        willLose: `${fmt(current.topX)} / ${fmt(current.bottomX)} → 0.500 / 0.500`,
      };
    }

    case 'parallel': {
      if (current.lockMode === 'free' && isTilted(current.topX, current.bottomX)) {
        const avg = (current.topX + current.bottomX) / 2;
        return {
          next: { topX: avg, bottomX: avg, lockMode: 'parallel' },
          needsConfirm: true,
          reason: 'Tilted split will be averaged',
          willLose:
            `Top ${fmt(current.topX)} / Bottom ${fmt(current.bottomX)} → ${fmt(avg)} (둘 다)`,
        };
      }
      // center → parallel (0.5 유지) or free → parallel with equal values
      return {
        next: { ...current, lockMode: 'parallel' },
        needsConfirm: false,
      };
    }

    case 'free':
      // center or parallel → free: 값 유지, 독립 편집 가능 상태로
      return { next: { ...current, lockMode: 'free' }, needsConfirm: false };
  }
}

/**
 * Predicate form requested by CALIBRATION_UX §6.1 — returns only the confirm
 * decision + dialog copy, without recomputing the next split values.
 * Primary consumer: confirm dialog rendering.
 */
export function shouldConfirmLockModeTransition(
  from: SplitLockMode,
  to: SplitLockMode,
  topX: number,
  bottomX: number,
): Pick<LockTransition, 'needsConfirm' | 'reason' | 'willLose'> {
  const t = transitionLockMode({ topX, bottomX, lockMode: from }, to);
  if (t.needsConfirm && t.reason !== undefined && t.willLose !== undefined) {
    return { needsConfirm: true, reason: t.reason, willLose: t.willLose };
  }
  return { needsConfirm: false };
}
